// features/common/PhotoViewModal.js
// Modal for viewing photos in full screen with zoom capability and caption editing

import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import ConfirmationModal from "./ConfirmationModal";
import { CachedImage } from "./CachedImage";
import { PhotoMetadataOverlay } from "./PhotoMetadataOverlay";
import { burnInMetadata } from "../../services/photoExportService";
import styles from "./PhotoViewModal.module.css";

/**
 * PhotoViewModal - Full-screen photo viewing modal with caption editing
 *
 * @param {object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {object} props.photo - FormPhotos instance to display
 * @param {function} props.onClose - Close handler
 * @param {boolean} props.canDownload - Whether user can download
 * @param {function} props.onDownload - Download handler
 * @param {boolean} props.isViewMode - Whether in view-only mode (no editing)
 * @param {function} props.onCaptionChange - Handler for caption changes (local state update)
 * @param {function} props.onCaptionBlur - Handler for caption blur (server sync)
 */
export const PhotoViewModal = ({
  isOpen,
  photo,
  onClose,
  canDownload = false,
  onDownload,
  isViewMode = false,
  onCaptionChange,
  onCaptionBlur,
}) => {
  const [localCaption, setLocalCaption] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showMetadata, setShowMetadata] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showDownloadChoice, setShowDownloadChoice] = useState(false);

  // Touch zoom/pan state
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);

  const imageContainerRef = useRef(null);
  const touchStateRef = useRef({
    initialDistance: 0,
    initialScale: 1,
    lastTouchCenter: { x: 0, y: 0 },
    touchStartPosition: { x: 0, y: 0 },
  });

  // Sync local caption with photo caption when photo changes or modal opens
  useEffect(() => {
    if (isOpen && photo) {
      setLocalCaption(photo.caption || "");
      setHasUnsavedChanges(false);
    }
  }, [isOpen, photo]);

  // Reset zoom and metadata state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
      setIsPanning(false);
      setShowMetadata(false);
    }
  }, [isOpen]);

  // Handle escape key to close
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown]);

  // Calculate distance between two touch points
  const getTouchDistance = (touch1, touch2) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Calculate center point between two touches
  const getTouchCenter = (touch1, touch2) => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
  };

  // Handle touch start
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      // Pinch zoom starting
      e.preventDefault();
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      const center = getTouchCenter(e.touches[0], e.touches[1]);
      touchStateRef.current.initialDistance = distance;
      touchStateRef.current.initialScale = scale;
      touchStateRef.current.lastTouchCenter = center;
    } else if (e.touches.length === 1 && scale > 1) {
      // Single touch pan when zoomed
      e.preventDefault();
      setIsPanning(true);
      touchStateRef.current.touchStartPosition = {
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      };
    }
  }, [scale, position]);

  // Handle touch move
  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2) {
      // Pinch zoom
      e.preventDefault();
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      const center = getTouchCenter(e.touches[0], e.touches[1]);

      const newScale = Math.max(
        1,
        Math.min(4, touchStateRef.current.initialScale * (distance / touchStateRef.current.initialDistance))
      );

      // Calculate position adjustment to zoom toward touch center
      const rect = imageContainerRef.current?.getBoundingClientRect();
      if (rect) {
        const centerX = center.x - rect.left;
        const centerY = center.y - rect.top;
        const deltaScale = newScale - scale;

        setPosition((prev) => ({
          x: prev.x - (centerX - rect.width / 2) * deltaScale * 0.5,
          y: prev.y - (centerY - rect.height / 2) * deltaScale * 0.5,
        }));
      }

      setScale(newScale);
      touchStateRef.current.lastTouchCenter = center;
    } else if (e.touches.length === 1 && isPanning && scale > 1) {
      // Pan
      e.preventDefault();
      const newX = e.touches[0].clientX - touchStateRef.current.touchStartPosition.x;
      const newY = e.touches[0].clientY - touchStateRef.current.touchStartPosition.y;

      // Apply boundaries to prevent panning too far
      const rect = imageContainerRef.current?.getBoundingClientRect();
      if (rect) {
        const maxX = (rect.width * (scale - 1)) / 2;
        const maxY = (rect.height * (scale - 1)) / 2;

        setPosition({
          x: Math.max(-maxX, Math.min(maxX, newX)),
          y: Math.max(-maxY, Math.min(maxY, newY)),
        });
      }
    }
  }, [scale, isPanning]);

  // Handle touch end
  const handleTouchEnd = useCallback((e) => {
    if (e.touches.length === 0) {
      setIsPanning(false);

      // Reset to fit if zoomed out past 1
      if (scale < 1) {
        setScale(1);
        setPosition({ x: 0, y: 0 });
      }
    }
  }, [scale]);

  // Handle overlay click (close on negative space)
  const handleOverlayClick = (e) => {
    // Only close if clicking directly on overlay (not on modal content)
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle double tap to reset zoom
  const lastTapRef = useRef(0);
  const handleImageClick = () => {
    const now = Date.now();
    const timeSince = now - lastTapRef.current;

    if (timeSince < 300 && timeSince > 0) {
      // Double tap - reset zoom
      setScale(1);
      setPosition({ x: 0, y: 0 });
    } else if (scale === 1) {
      // Single tap when not zoomed - zoom to 2x
      setScale(2);
    }

    lastTapRef.current = now;
  };

  // Handle download click - show choice if photo has geo data
  const handleDownloadClick = (e) => {
    e.stopPropagation();
    if (!photo) return;

    const hasGeoData = typeof photo.hasGeoData === "function" && photo.hasGeoData();
    if (hasGeoData) {
      setShowDownloadChoice(true);
    } else if (onDownload) {
      onDownload(photo);
    }
  };

  // Download original photo (from choice modal)
  const handleDownloadOriginal = () => {
    setShowDownloadChoice(false);
    if (onDownload && photo) {
      onDownload(photo);
    }
  };

  // Download photo with burned-in metadata (from choice modal)
  const handleDownloadWithMetadata = async () => {
    setShowDownloadChoice(false);
    if (!photo || isExporting) return;

    setIsExporting(true);
    try {
      const url = typeof photo.getDisplayUrl === "function"
        ? photo.getDisplayUrl()
        : (photo.localPreviewUrl || photo.presignedUrl || photo.presigned_url || null);
      if (!url) return;

      const blob = await burnInMetadata(url, {
        latitude: photo.latitude,
        longitude: photo.longitude,
        altitude: photo.altitude,
        bearing: photo.bearing,
        accuracy: photo.accuracy,
        address: photo.address,
        capturedAt: photo.capturedAt,
        capturedAtLocal: photo.capturedAtLocal,
      });

      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `photo_${photo.id}_geo.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Export with metadata failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle caption input change (local state only)
  const handleCaptionInputChange = (e) => {
    const newCaption = e.target.value;
    setLocalCaption(newCaption);
    setHasUnsavedChanges(true);
    if (onCaptionChange && photo) {
      onCaptionChange(photo.id, newCaption);
    }
  };

  // Handle save button click (sync to server)
  const handleSaveCaption = (e) => {
    e.stopPropagation();
    if (onCaptionBlur && photo) {
      onCaptionBlur(photo.id, localCaption);
      setHasUnsavedChanges(false);
      // Close modal after save
      onClose();
    }
  };

  if (!isOpen || !photo) return null;

  const imageUrl = typeof photo.getDisplayUrl === "function"
    ? photo.getDisplayUrl()
    : (photo.localPreviewUrl || photo.presignedUrl || photo.presigned_url || null);

  const hasGeo = typeof photo.hasGeoData === "function" && photo.hasGeoData();

  // Render via portal to document.body to escape ancestor overflow/stacking context issues
  // (e.g. nested scroll containers in DelForm > MiscTab clipping the modal on iPad Safari)
  return createPortal(
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button
          className={styles.closeButton}
          onClick={onClose}
          type="button"
          aria-label="Close"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Download button */}
        {canDownload && (
          <button
            className={styles.downloadButton}
            onClick={handleDownloadClick}
            type="button"
            aria-label="Download"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
        )}

        {/* Metadata toggle button - only for photos with geo data */}
        {hasGeo && (
          <button
            className={`${styles.metadataToggle} ${showMetadata ? styles.metadataToggleActive : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setShowMetadata(!showMetadata);
            }}
            type="button"
            aria-label="Toggle metadata"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </button>
        )}

        {/* Image container */}
        <div
          ref={imageContainerRef}
          className={styles.imageContainer}
          onClick={handleImageClick}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transition: isPanning ? 'none' : 'transform 0.2s ease-out',
            cursor: scale > 1 ? 'move' : 'pointer',
          }}
        >
          {/* Metadata overlay */}
          {showMetadata && hasGeo && (
            <PhotoMetadataOverlay
              address={photo.address}
              latitude={photo.latitude}
              longitude={photo.longitude}
              altitude={photo.altitude}
              accuracy={photo.accuracy}
              bearing={photo.bearing}
              capturedAt={photo.capturedAt}
              capturedAtLocal={photo.capturedAtLocal}
              showExtended={true}
            />
          )}
          <CachedImage
            photoId={photo.id}
            src={imageUrl}
            alt={photo.caption || "Photo"}
            className={styles.image}
            draggable={false}
          />
        </div>

        {/* Caption section */}
        <div className={styles.captionContainer}>
          {!isViewMode ? (
            <div className={styles.captionInputWrapper}>
              <textarea
                className={styles.captionInput}
                placeholder="Add a caption..."
                value={localCaption}
                onChange={handleCaptionInputChange}
                onClick={(e) => e.stopPropagation()}
                rows={3}
              />
              <button
                type="button"
                className={`${styles.saveButton} ${hasUnsavedChanges ? styles.saveButtonActive : ""}`}
                onClick={handleSaveCaption}
                disabled={!hasUnsavedChanges}
              >
                Save
              </button>
            </div>
          ) : (
            localCaption && <p className={styles.caption}>{localCaption}</p>
          )}
        </div>

        {/* Zoom hint */}
        <div className={styles.zoomHint}>
          {scale > 1 ? "Pinch or double-tap to reset" : "Pinch to zoom or double-tap"}
        </div>

        {/* Download choice modal - shown when downloading a photo with geo data */}
        <ConfirmationModal
          isOpen={showDownloadChoice}
          onCancel={handleDownloadOriginal}
          onConfirm={handleDownloadWithMetadata}
          title="Download Photo"
          message="This photo has location data. Choose a download option:"
          cancelText="Original"
          confirmText="With Location Data"
          confirmButtonClass="confirmButton"
        />
      </div>
    </div>,
    document.body
  );
};

export default PhotoViewModal;